import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Clock,
  ArrowRight,
  Calendar,
  X,
} from "lucide-react";

const AttendanceTimeline = ({
  attendanceHistory,
  navigate,
  attendanceStatus,
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // --- Helper Functions (No changes) ---
  const getAttendanceStatusInfo = (record) => {
    if (!record.punchOutTime || !record.punchoutStatus) {
      return {
        status: "Active",
        color: "text-blue-600",
        bgColor: "bg-blue-100",
        barColor: "bg-blue-500",
      };
    }
    switch (record.punchoutStatus?.toUpperCase()) {
      case "SUCCESSFULL":
      case "SUCCESSFUL":
      case "APPROVED":
        return {
          status: "Completed",
          color: "text-emerald-600",
          bgColor: "bg-emerald-100",
          barColor: "bg-emerald-500",
        };
      case "PENDING":
      case "REQUESTED":
        return {
          status: "Pending",
          color: "text-amber-600",
          bgColor: "bg-amber-100",
          barColor: "bg-amber-500",
        };
      case "FAILED":
      case "REJECTED":
      case "CANCELED":
        return {
          status: "Incomplete",
          color: "text-red-600",
          bgColor: "bg-red-100",
          barColor: "bg-red-500",
        };
      default:
        return {
          status: record.punchoutStatus || "Unknown",
          color: "text-slate-600",
          bgColor: "bg-slate-100",
          barColor: "bg-slate-500",
        };
    }
  };

  const formatAttendanceDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const convertTo24Hour = (time12h) => {
    if (!time12h) return null;
    const [time, modifier] = time12h.trim().split(" ");
    let [hours, minutes] = time.split(":");
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes || "0", 10);

    if (modifier?.toLowerCase() === "pm" && hours !== 12) hours += 12;
    if (modifier?.toLowerCase() === "am" && hours === 12) hours = 0;
    return hours + minutes / 60;
  };

  const calculateWorkDuration = (punchIn, punchOut) => {
    if (!punchIn || !punchOut) return null;
    const startHour = convertTo24Hour(punchIn);
    const endHour = convertTo24Hour(punchOut);
    if (startHour === null || endHour === null) return null;

    let diff = endHour - startHour;
    if (diff < 0) diff += 24;
    const hours = Math.floor(diff);
    const minutes = Math.round((diff - hours) * 60);

    if (isNaN(hours) || isNaN(minutes)) return null;
    return `${hours}h ${minutes}m`;
  };

  // --- Dynamic Timeline Calculation (No changes) ---
  const officeTiming = {
    shiftFrom: attendanceStatus?.officeTiming?.shiftForm || "09:30 am",
    shiftTo: attendanceStatus?.officeTiming?.shiftTo || "06:30 pm",
  };

  const officeStartHour = convertTo24Hour(officeTiming.shiftFrom) || 9.5;
  const officeEndHour = convertTo24Hour(officeTiming.shiftTo) || 18.5;

  const timelineRange = {
    start: Math.floor(officeStartHour) - 1,
    end: Math.ceil(officeEndHour) + 1,
  };

  const getTimelinePosition = (time) => {
    const hour = convertTo24Hour(time);
    if (hour === null) return 0;
    const { start, end } = timelineRange;
    const position = ((hour - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const officeStartPos = getTimelinePosition(officeTiming.shiftFrom);
  const officeEndPos = getTimelinePosition(officeTiming.shiftTo);

  const sortedHistory = useMemo(() => {
    if (!attendanceHistory) return [];
    return [...attendanceHistory].sort(
      (a, b) => new Date(b.attendanceDate) - new Date(a.attendanceDate)
    );
  }, [attendanceHistory]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsMoreOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isMoreOpen]);

  const renderAttendanceRecord = (record) => {
    const statusInfo = getAttendanceStatusInfo(record);
    const workDuration = calculateWorkDuration(
      record.punchInTime,
      record.punchOutTime
    );
    const punchInPosition = getTimelinePosition(record.punchInTime);
    const punchOutPosition = record.punchOutTime
      ? getTimelinePosition(record.punchOutTime)
      : null;

    return (
      <div
        key={record.attendanceId}
        className="group bg-slate-50 p-3 rounded-lg border border-slate-200/80"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800 text-sm">
              {formatAttendanceDate(record.attendanceDate)}
            </span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}
            >
              {statusInfo.status}
            </span>
          </div>
          {workDuration && (
            <span className="text-xs font-semibold text-slate-500 bg-white border px-2 py-0.5 rounded-md">
              {workDuration}
            </span>
          )}
        </div>

        <div className="relative pt-2 pb-4">
          <div className="relative w-full h-1.5 bg-slate-200 rounded-full">
            <div
              className="absolute h-full bg-blue-200 rounded-full"
              style={{
                left: `${officeStartPos}%`,
                width: `${officeEndPos - officeStartPos}%`,
              }}
            />
            {punchOutPosition !== null && (
              <div
                className={`absolute h-full ${statusInfo.barColor} rounded-full`}
                style={{
                  left: `${Math.min(punchInPosition, punchOutPosition)}%`,
                  width: `${Math.abs(punchOutPosition - punchInPosition)}%`,
                }}
              />
            )}
          </div>

          <div
            className="absolute top-1/2 -mt-2 -translate-x-1/2 z-10"
            style={{ left: `${punchInPosition}%` }}
          >
            <div className="w-4 h-4 bg-white border-2 border-emerald-500 rounded-full shadow" />
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
              {record.punchInTime}
            </div>
          </div>

          {punchOutPosition !== null && (
            <div
              className="absolute top-1/2 -mt-2 -translate-x-1/2 z-10"
              style={{ left: `${punchOutPosition}%` }}
            >
              <div className="w-4 h-4 bg-white border-2 border-red-500 rounded-full shadow" />
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                {record.punchOutTime}
              </div>
            </div>
          )}

          <div className="absolute top-full w-full -mt-2">
            <span
              className="absolute -translate-x-1/2 text-[10px] font-medium text-slate-500 whitespace-nowrap"
              style={{ left: `${officeStartPos}%` }}
            >
              {officeTiming.shiftFrom}
            </span>
            <span
              className="absolute -translate-x-1/2 text-[10px] font-medium text-slate-500 whitespace-nowrap"
              style={{ left: `${officeEndPos}%` }}
            >
              {officeTiming.shiftTo}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-attendance-timeline">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">
            Attendance Log
          </h3>
        </div>
        {sortedHistory.length > 4 ? (
          <button
            type="button"
            onClick={() => setIsMoreOpen(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
          >
            <span>More ({sortedHistory.length - 4})</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate && navigate("/ess/my-logs")}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
          >
            <span>View All</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Content Area with Grid Layout */}
      <div className="flex-1 overflow-hidden p-3">
        {sortedHistory && sortedHistory.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sortedHistory.slice(0, 4).map(renderAttendanceRecord)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Clock className="w-8 h-8 text-slate-400 mb-2" />
            <h4 className="font-semibold text-slate-600">No Records Found</h4>
            <p className="text-sm text-slate-500">
              Your logs will appear here.
            </p>
          </div>
        )}
      </div>

      {isMoreOpen &&
        createPortal(
        <div
          className="dashboard-attendance-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsMoreOpen(false);
          }}
        >
          <section
            className="dashboard-attendance-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-attendance-modal-title"
          >
            <header className="dashboard-attendance-modal__header">
              <div>
                <h2 id="dashboard-attendance-modal-title">Attendance log</h2>
                <p>{sortedHistory.length} records, newest first</p>
              </div>
              <button
                type="button"
                aria-label="Close attendance log"
                onClick={() => setIsMoreOpen(false)}
              >
                <X size={18} />
              </button>
            </header>
            <div className="dashboard-attendance-modal__list">
              {sortedHistory.map(renderAttendanceRecord)}
            </div>
            <footer className="dashboard-attendance-modal__footer">
              <button
                type="button"
                onClick={() => {
                  setIsMoreOpen(false);
                  navigate?.("/ess/my-logs");
                }}
              >
                Open full attendance page <ArrowRight size={14} />
              </button>
            </footer>
          </section>
        </div>,
        document.body
        )}
    </div>
  );
};

export default AttendanceTimeline;
